/**
 * <em-slideshow> — full width slideshow with animated transitions, autoplay (with per-slide video
 * duration), progress markers, swipe / keyboard navigation and theme editor support.
 */

const SWIPE_DISTANCE = 80;
const SWIPE_TIME = 500;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Pausable countdown used for autoplay. */
class Countdown {
  constructor(onDone) {
    this.onDone = onDone;
    this.state = 'idle';
    this.remaining = 0;
  }

  start(seconds) {
    clearTimeout(this.timeout);
    this.remaining = Math.max(0, seconds * 1000);
    this.state = 'running';
    this.startedAt = performance.now();
    this.timeout = setTimeout(() => this.finish(), this.remaining);
  }

  pause() {
    if (this.state !== 'running') return false;
    clearTimeout(this.timeout);
    this.remaining -= performance.now() - this.startedAt;
    this.state = 'paused';
    return true;
  }

  resume() {
    if (this.state !== 'paused') return false;
    this.state = 'running';
    this.startedAt = performance.now();
    this.timeout = setTimeout(() => this.finish(), Math.max(0, this.remaining));
    return true;
  }

  stop() {
    clearTimeout(this.timeout);
    this.state = 'stopped';
  }

  finish() {
    this.state = 'idle';
    this.onDone();
  }
}

/** Builds WAAPI animations for one slide change. Returns the list of Animation objects. */
function buildTransition(type, from, to, direction) {
  const flip = direction === 'next' ? 1 : -1;
  const fromBox = from.firstElementChild || from;
  const toBox = to.firstElementChild || to;
  const animations = [];

  const run = (element, keyframes, seconds, easing, delay = 0, fill = 'both') => {
    animations.push(
      element.animate(keyframes, { duration: seconds * 1000, delay: delay * 1000, easing, fill })
    );
  };

  switch (type) {
    case 'vertical_reveal': {
      const easeIn = 'cubic-bezier(0.55, 0.085, 0.68, 0.53)';
      const easeOut = 'cubic-bezier(0.19, 1, 0.22, 1)';
      run(from, [{ transform: 'translateY(0)' }, { transform: `translateY(${-flip * 100}%)` }], 0.3, easeIn);
      run(fromBox, [{ transform: 'translateY(0)' }, { transform: `translateY(${flip * 75}%)` }], 0.3, easeIn);
      run(to, [{ transform: `translateY(${flip * 100}%)` }, { transform: 'translateY(0)' }], 0.7, easeOut, 0.4);
      run(toBox, [{ transform: `translateY(${-flip * 75}%)` }, { transform: 'translateY(0)' }], 0.7, easeOut, 0.4);
      break;
    }

    case 'curtain': {
      const easing = 'cubic-bezier(0.77, 0, 0.175, 1)';
      const seconds = window.innerWidth < 699 ? 0.8 : 1;
      run(from, [{ transform: 'translateX(0)' }, { transform: `translateX(${-flip * 100}%)` }], seconds, easing);
      run(fromBox, [{ transform: 'translateX(0)' }, { transform: `translateX(${flip * 30}%)` }], seconds, easing);
      run(to, [{ transform: `translateX(${flip * 100}%)` }, { transform: 'translateX(0)' }], seconds, easing);
      run(toBox, [{ transform: `translateX(${-flip * 30}%)` }, { transform: 'translateX(0)' }], seconds, easing);
      break;
    }

    case 'distortion': {
      const easing = 'cubic-bezier(0.645, 0.045, 0.355, 1)';
      const seconds = window.innerWidth < 699 ? 0.6 : 0.8;
      fromBox.style.transformOrigin = direction === 'next' ? '100% 50%' : '0% 50%';
      toBox.style.transformOrigin = direction === 'next' ? '0% 50%' : '100% 50%';
      run(from, [{ transform: 'translateX(0)' }, { transform: `translateX(${-flip * 100}%)` }], seconds, easing);
      run(fromBox, [{ transform: 'scaleX(1)' }, { transform: 'scaleX(2.5)' }], seconds, easing);
      run(to, [{ transform: `translateX(${flip * 100}%)` }, { transform: 'translateX(0)' }], seconds, easing);
      run(
        toBox,
        [{ transform: `translateX(${-flip * 100}%) scaleX(2.5)` }, { transform: 'translateX(0) scaleX(1)' }],
        seconds,
        easing
      );
      break;
    }

    case 'perspective': {
      const easing = 'cubic-bezier(0.77, 0, 0.175, 1)';
      run(
        from,
        [{ transform: 'scale(1)', opacity: 1 }, { transform: 'scale(0.9)', opacity: 0.3 }],
        0.15,
        'cubic-bezier(0.445, 0.05, 0.55, 0.95)'
      );
      run(
        from,
        [
          { transform: 'translateY(0) scale(0.9)', opacity: 0.3 },
          { transform: 'translateY(-20%) scale(0.9)', opacity: 0 },
        ],
        0.7,
        easing,
        0.25,
        'forwards'
      );
      run(to, [{ transform: 'translateY(100%) scale(1)' }, { transform: 'translateY(0) scale(1)' }], 0.7, easing, 0.25);
      run(toBox, [{ transform: 'translateY(-50%)' }, { transform: 'translateY(0)' }], 0.7, easing, 0.25);
      break;
    }

    case 'scale_down': {
      const easing = 'cubic-bezier(0.645, 0.045, 0.355, 1)';
      run(from, [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(0.7)' }], 0.5, easing);
      run(to, [{ transform: `translateY(${flip * 100}%)` }, { transform: 'translateY(0)' }], 0.7, easing);
      break;
    }

    default: {
      run(from, [{ opacity: 1 }, { opacity: 0 }], 0.3, 'ease');
      run(to, [{ opacity: 0 }, { opacity: 1 }], 0.3, 'ease');
    }
  }

  return animations;
}

class EmSlideshow extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.slides.forEach((slide, index) => slide.classList.toggle('is-active', index === this.currentIndex));

    this.autoplaySeconds = parseFloat(this.dataset.autoplay || '') || 0;
    this.countdown = new Countdown(() => this.next());
    this.isTransitioning = false;
    this.isUserPaused = false;
    this.isHoverPaused = false;
    this.isEditorStopped = false;
    this.hasStarted = false;
    this.timerToken = 0;

    this.setupVideos(signal);
    this.setupControls(signal);
    this.playSlideVideos(this.activeSlide);

    if (this.slides.length > 1) {
      this.setupInteractions(signal);
    }

    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      this.hasStarted = true;
      this.preloadAround(this.currentIndex);
      this.startTimer(this.activeSlide);
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.countdown?.stop();
    this.progressAnimation?.cancel();
  }

  get slides() {
    return Array.from(this.querySelectorAll(':scope > .em-slide'));
  }

  get activeSlide() {
    return this.slides[this.currentIndex];
  }

  get transition() {
    return reducedMotion() ? 'fade' : this.dataset.transition || 'fade';
  }

  get markers() {
    return Array.from(this.querySelectorAll('[data-em-slide-to]'));
  }

  get toggleButton() {
    return this.querySelector('[data-em-autoplay-toggle]');
  }

  /* ---------- Navigation ---------- */

  next({ instant = false } = {}) {
    const count = this.slides.length;
    return this.select((this.currentIndex + 1) % count, { instant, direction: 'next' });
  }

  previous({ instant = false } = {}) {
    const count = this.slides.length;
    return this.select((this.currentIndex - 1 + count) % count, { instant, direction: 'previous' });
  }

  async select(index, { instant = false, direction = null } = {}) {
    const slides = this.slides;
    if (!slides[index] || index === this.currentIndex) return;
    if (this.isTransitioning && this.transition !== 'fade') return;

    const from = slides[this.currentIndex];
    const to = slides[index];
    const way = direction || (index > this.currentIndex ? 'next' : 'previous');

    this.currentIndex = index;
    this.isUserPaused = false;
    this.updateMarkers();
    this.updateToggle();
    this.stopTimer();
    this.preloadAround(index);
    this.playSlideVideos(to);

    this.isTransitioning = true;
    to.classList.add('is-active');
    const animations = instant ? [] : buildTransition(this.transition, from, to, way);

    if (this.hasStarted) this.startTimer(to);

    await Promise.all(animations.map((animation) => animation.finished.catch(() => {})));

    if (this.activeSlide !== from) from.classList.remove('is-active');
    animations.forEach((animation) => animation.cancel());
    [from, to].forEach((slide) => slide.firstElementChild?.style.removeProperty('transform-origin'));
    this.isTransitioning = false;
  }

  /* ---------- Autoplay ---------- */

  async durationFor(slide) {
    if (slide.dataset.slideType === 'video') {
      const video = Array.from(slide.querySelectorAll('video')).find((element) => element.offsetParent !== null)
        || slide.querySelector('video');

      if (video) {
        if (Number.isNaN(video.duration) || !video.duration) {
          await new Promise((resolve) => video.addEventListener('loadedmetadata', resolve, { once: true }));
        }
        if (Number.isFinite(video.duration)) return video.duration;
      }
    }

    return this.autoplaySeconds;
  }

  async startTimer(slide) {
    if (!this.autoplaySeconds || this.slides.length < 2 || this.isEditorStopped) return;

    const token = ++this.timerToken;
    const seconds = await this.durationFor(slide);
    if (token !== this.timerToken || slide !== this.activeSlide) return;

    this.countdown.start(seconds);
    this.animateProgress(seconds);

    if (this.isUserPaused || this.isHoverPaused || document.hidden) this.pauseTimer();
  }

  stopTimer() {
    this.timerToken++;
    this.countdown.stop();
    this.progressAnimation?.cancel();
    this.progressAnimation = null;
  }

  pauseTimer() {
    this.countdown.pause();
    this.progressAnimation?.pause();
    this.activeSlide?.querySelectorAll('video').forEach((video) => video.pause());
  }

  resumeTimer() {
    if (this.isUserPaused || this.isHoverPaused || this.isEditorStopped) return;
    this.countdown.resume();
    this.progressAnimation?.play();
    this.activeSlide?.querySelectorAll('video').forEach((video) => this.safePlay(video));
  }

  animateProgress(seconds) {
    this.progressAnimation?.cancel();
    const bar = this.markers[this.currentIndex]?.querySelector('.em-slideshow-marker__progress');
    if (!bar) return;

    this.progressAnimation = bar.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], {
      duration: seconds * 1000,
      easing: 'linear',
      fill: 'forwards',
    });
  }

  /* ---------- Videos ---------- */

  setupVideos(signal) {
    this.querySelectorAll('.em-slide__video video').forEach((video) => {
      video.addEventListener('playing', () => video.parentElement.classList.add('is-playing'), { signal });
    });
  }

  playSlideVideos(slide) {
    this.querySelectorAll('.em-slide video').forEach((video) => {
      if (!slide?.contains(video)) video.pause();
    });

    if (slide?.dataset.slideType !== 'video') return;

    slide.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      try {
        video.currentTime = 0;
      } catch (error) {
        /* metadata not loaded yet */
      }
      this.safePlay(video);
    });
  }

  safePlay(video) {
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  }

  preloadAround(index) {
    const slides = this.slides;
    const count = slides.length;
    [index - 1, index, index + 1].forEach((position) => {
      const slide = slides[(position + count) % count];
      slide?.querySelectorAll('img[loading="lazy"]').forEach((img) => img.removeAttribute('loading'));
      slide?.querySelectorAll('video[preload="none"]').forEach((video) => video.setAttribute('preload', 'metadata'));
    });
  }

  /* ---------- Controls ---------- */

  setupControls(signal) {
    this.markers.forEach((marker) => {
      marker.addEventListener(
        'click',
        () => this.select(parseInt(marker.dataset.emSlideTo, 10)),
        { signal }
      );
    });

    this.toggleButton?.addEventListener(
      'click',
      () => {
        if (this.isUserPaused) {
          this.isUserPaused = false;
          this.resumeTimer();
        } else {
          this.isUserPaused = true;
          this.pauseTimer();
        }
        this.updateToggle();
      },
      { signal }
    );
  }

  updateMarkers() {
    this.markers.forEach((marker, index) => {
      marker.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
    });
  }

  updateToggle() {
    this.toggleButton?.setAttribute('aria-pressed', this.isUserPaused ? 'true' : 'false');
  }

  /* ---------- Interactions ---------- */

  setupInteractions(signal) {
    this.tabIndex = 0;

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.target !== this) return;
        if (event.key === 'ArrowLeft') this.previous();
        if (event.key === 'ArrowRight') this.next();
      },
      { signal }
    );

    let gesture = null;
    let suppressClick = false;

    this.addEventListener(
      'pointerdown',
      (event) => {
        if (event.target.closest('[data-em-slideshow-controls]')) return;
        gesture = { x: event.clientX, y: event.clientY, time: performance.now() };
        suppressClick = false;
      },
      { signal }
    );

    const endGesture = (event) => {
      if (!gesture) return;
      const deltaX = event.clientX - gesture.x;
      const deltaY = event.clientY - gesture.y;
      const elapsed = performance.now() - gesture.time;
      gesture = null;

      if (Math.abs(deltaX) > 10) suppressClick = true;
      if (elapsed > SWIPE_TIME || Math.abs(deltaY) >= SWIPE_DISTANCE) return;

      if (deltaX < -SWIPE_DISTANCE) this.next();
      if (deltaX > SWIPE_DISTANCE) this.previous();
    };

    this.addEventListener('pointerup', endGesture, { signal });
    this.addEventListener('pointercancel', () => (gesture = null), { signal });
    this.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    this.addEventListener(
      'click',
      (event) => {
        if (suppressClick) {
          event.preventDefault();
          suppressClick = false;
        }
      },
      { signal, capture: true }
    );

    if (this.autoplaySeconds && this.hasAttribute('data-pause-on-hover')) {
      this.addEventListener(
        'mouseenter',
        () => {
          this.isHoverPaused = true;
          this.pauseTimer();
        },
        { signal }
      );
      this.addEventListener(
        'mouseleave',
        () => {
          this.isHoverPaused = false;
          this.resumeTimer();
        },
        { signal }
      );
    }

    document.addEventListener(
      'visibilitychange',
      () => (document.hidden ? this.pauseTimer() : this.resumeTimer()),
      { signal }
    );

    if (window.Shopify?.designMode) {
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          const slide = event.target.closest?.('.em-slide');
          if (!slide) return;
          this.isEditorStopped = true;
          this.stopTimer();
          this.select(this.slides.indexOf(slide), { instant: Boolean(event.detail?.load) });
        },
        { signal }
      );
      this.addEventListener(
        'shopify:block:deselect',
        () => {
          this.isEditorStopped = false;
          this.startTimer(this.activeSlide);
        },
        { signal }
      );
    }
  }
}

if (!customElements.get('em-slideshow')) {
  customElements.define('em-slideshow', EmSlideshow);
}
